import { PAGE_SIZE } from '@/constants/pageSize';
import {
  useAnswerPartnershipRequest,
  useFindReceivedPartnershipRequests,
} from '@/hooks/useDistributors';
import useTableControls from '@/hooks/useTableControls';
import Loader from '../ui/Loader';
import StyledTable from '../styled/StyledTable';
import { RECEIVED_PARTNERSHIP_REQUESTS_COLUMNS } from '@/types/distributors/ReceivedPartnershipRequestsColumnDef';
import StyledNextPreviousButtons from '../styled/StyledNextPreviousButtons';
import { useState } from 'react';
import PartnershipAnswerModal from './PartnershipAnswerModal';
import { useToast } from '@/hooks/useToast';
import DistributorInfoModal from './modals/DistributorInfoModal';
import { useTranslation } from '@/i18n';
import { useTranslatedConst } from '@/hooks/useTranslatedConst';

const ReceivedPartnershipRequests = () => {
  const { t } = useTranslation();
  const getTranslatedConst = useTranslatedConst();
  const { page, setPage, setSorting, sorting, sortField, sortOrder } =
    useTableControls();

  const {
    data: receivedPartnershipRequestsResponse,
    isSuccess,
    isLoading,
  } = useFindReceivedPartnershipRequests({
    page,
    pageSize: PAGE_SIZE,
    sortField,
    sortOrder,
  });

  const [
    distributorToAnswerPartnershipRequest,
    setDistributorToAnswerPartnershipRequest,
  ] = useState<string | null>(null);
  const [distributorToViewId, setDistributorToViewId] = useState<string | null>(
    null,
  );

  const [partnershipRequestAnswer, setPartnershipRequestAnswer] = useState<
    boolean | null
  >(null);

  const { mutateAsync: answerPartnershipRequest } =
    useAnswerPartnershipRequest();
  const { toastAsync } = useToast();

  return isLoading ? (
    <Loader />
  ) : (
    isSuccess && (
      <div className="flex flex-col gap-6 w-full h-auto pt-4">
        <StyledTable
          columns={getTranslatedConst(RECEIVED_PARTNERSHIP_REQUESTS_COLUMNS)}
          data={receivedPartnershipRequestsResponse.requests}
          getRowId={(row) => row.id}
          sorting={sorting}
          setSorting={setSorting}
          onAcceptRow={(row) => {
            setDistributorToAnswerPartnershipRequest(row.original.id);
            setPartnershipRequestAnswer(true);
          }}
          onRefuseRow={(row) => {
            setDistributorToAnswerPartnershipRequest(row.original.id);
            setPartnershipRequestAnswer(false);
          }}
          onViewRow={(row) => {
            setDistributorToViewId(row.original.id);
          }}
        />
        <StyledNextPreviousButtons
          page={page}
          pageSize={PAGE_SIZE}
          setPage={setPage}
          dataLength={receivedPartnershipRequestsResponse.total}
          label="demande"
        />
        {distributorToViewId && (
          <DistributorInfoModal
            distributorId={distributorToViewId}
            closeModal={() => setDistributorToViewId(null)}
          />
        )}
        {distributorToAnswerPartnershipRequest !== null &&
          partnershipRequestAnswer !== null && (
            <PartnershipAnswerModal
              closeModal={() => {
                setDistributorToAnswerPartnershipRequest(null);
                setPartnershipRequestAnswer(null);
              }}
              onConfirm={async () => {
                await toastAsync(
                  answerPartnershipRequest({
                    distributorId: distributorToAnswerPartnershipRequest,
                    isAccepted: partnershipRequestAnswer,
                  }),
                  {
                    success: partnershipRequestAnswer
                      ? t('distributors.partnershipRequests.acceptedSuccess')
                      : t('distributors.partnershipRequests.refusedSuccess'),
                  },
                );
              }}
              isAccepted={partnershipRequestAnswer}
            />
          )}
      </div>
    )
  );
};

export default ReceivedPartnershipRequests;
